import type { ChangesBody, Cursor, Dataset, PageCursor, SnapshotBody } from './dataset.js';

/**
 * Keeping the local `Dataset` level with the server (ADR-0025).
 *
 * The rule this exists to enforce: **the same code paths run whether or not
 * there is a connection.** Every screen reads the `Dataset`; the connection
 * only decides whether the `Dataset` is being kept up to date. An
 * offline-only fallback path would be a path exercised rarely, and a rarely
 * exercised path is a broken one — which is precisely how the feature this
 * replaces came to be broken.
 *
 * So there is no "offline mode" here. There is a store, and a thing that
 * catches it up when it can.
 */

export type SyncState = 'never' | 'syncing' | 'synced' | 'failed';

export interface ReplicaSyncOptions {
  dataset: Dataset;
  fetchSnapshot: (from?: { after: PageCursor; cursor: Cursor }) => Promise<SnapshotBody>;
  fetchChanges: (
    since: Cursor, within?: { until: Cursor; after: PageCursor },
  ) => Promise<ChangesBody>;
  /** How long a poke waits for its neighbours. */
  debounceMs?: number;
  /** Test seam; `setTimeout` in a browser. */
  schedule?: (fn: () => void, ms: number) => unknown;
}

const DEFAULT_DEBOUNCE_MS = 400;

/**
 * A ceiling on how many pages one catch-up will follow.
 *
 * Not a cap on how much data can arrive — the pages are followed until the
 * server stops offering more, which is the whole point. This is a guard
 * against a server that keeps handing back a `next` that makes no progress:
 * without it that is an infinite loop holding the sync lock, which presents
 * to the user as an app that has hung rather than one that failed. Failing
 * is recoverable; hanging is not. At 2000 rows a page this is forty million
 * rows, so no honest ledger can reach it.
 */
const MAX_PAGES = 20_000;

export class ReplicaSync {
  private state: SyncState = 'never';
  private readonly listeners = new Set<() => void>();
  /** The sync in flight, so that concurrent callers join it rather than racing it. */
  private inFlight: Promise<boolean> | undefined;
  private pokePending = false;
  private lastError: unknown;

  constructor(private readonly opts: ReplicaSyncOptions) {}

  current(): SyncState {
    return this.state;
  }

  error(): unknown {
    return this.lastError;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private set(state: SyncState): void {
    this.state = state;
    for (const fn of this.listeners) fn();
  }

  /**
   * Catch up: a delta when there is a watermark to start from, a full
   * snapshot when there is not.
   *
   * Concurrent callers get the same promise. Two snapshots in flight at once
   * would each clear the store the other is filling, and the loser's rows
   * would land after the winner's `clear()` — a half-populated ledger with a
   * watermark claiming it is whole.
   */
  async sync(): Promise<boolean> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.run();
    try {
      return await this.inFlight;
    } finally {
      this.inFlight = undefined;
    }
  }

  /**
   * Take a whole snapshot, however many responses it comes in.
   *
   * The cursor from the FIRST page is the one adopted at the end, and it is
   * sent back with each request so the server cannot quietly move it.
   * Later pages are read later and may already contain changes above that
   * cursor, which costs nothing: the first delta re-sends them and applying
   * a row twice is applying it once. The reverse — adopting a cursor from
   * the last page — would claim ground the earlier pages never covered.
   */
  private async snapshot(): Promise<void> {
    await this.opts.dataset.beginSnapshot();
    let page = await this.opts.fetchSnapshot();
    const cursor = page.cursor;
    await this.opts.dataset.addRows(page.rows);
    for (let seen = 0; page.next; seen += 1) {
      if (seen >= MAX_PAGES) throw new Error('replica: snapshot did not terminate');
      page = await this.opts.fetchSnapshot({ after: page.next, cursor });
      await this.opts.dataset.addRows(page.rows);
    }
    await this.opts.dataset.finishSnapshot(cursor);
  }

  /**
   * Catch up from `since`, however many responses that takes. False means the
   * server refused the cursor and the caller must snapshot instead.
   *
   * The cursor is adopted once, after the last page. An interruption part way
   * through therefore leaves it exactly where it was, and the next attempt
   * asks for the same window again — rows already applied are simply applied
   * again. That is the property that makes a partial catch-up safe, and it is
   * why the pages must not each advance it.
   */
  private async delta(since: Cursor): Promise<boolean> {
    let page = await this.opts.fetchChanges(since);
    if (page.restart) return false;
    await this.opts.dataset.applyChanges(page.rows);
    for (let seen = 0; page.next; seen += 1) {
      if (seen >= MAX_PAGES) throw new Error('replica: delta did not terminate');
      page = await this.opts.fetchChanges(since, page.next);
      if (page.restart) return false;
      await this.opts.dataset.applyChanges(page.rows);
    }
    await this.opts.dataset.commitCursor(page.cursor);
    return true;
  }

  private async run(): Promise<boolean> {
    this.set('syncing');
    try {
      const mark = this.opts.dataset.cursor();
      if (this.opts.dataset.needsSnapshot() || mark === undefined) {
        await this.snapshot();
      } else if (!await this.delta(mark)) {
        // The server could not serve from that cursor. Anything short of a
        // full snapshot here leaves the device quietly believing it is
        // current, which is the worst of the three possible states to be in.
        await this.snapshot();
      }
      this.lastError = undefined;
      this.set('synced');
      return true;
    } catch (err) {
      /*
       * A failed sync changes nothing.
       *
       * The watermark is only ever advanced by a body that arrived in full, so
       * a drop half way through leaves the device exactly where it was: the
       * rows it had, the cursor it had, and the next attempt asks the same
       * question. Clearing anything on failure would turn a flaky connection
       * into data loss.
       */
      this.lastError = err;
      this.set('failed');
      return false;
    }
  }

  /**
   * "Something changed" — from the live feed, a local save, or coming back
   * online. Debounced, because a document-group commit announces every record
   * it touched and a transaction with eight splits should cost one delta, not
   * nine.
   */
  poke(): void {
    if (this.pokePending) return;
    this.pokePending = true;
    const schedule = this.opts.schedule ?? ((fn, ms) => setTimeout(fn, ms));
    schedule(() => {
      this.pokePending = false;
      void this.sync();
    }, this.opts.debounceMs ?? DEFAULT_DEBOUNCE_MS);
  }
}
